import {
	type InfiniteData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { clubApi } from "../../../club-api-client";
import type { RoomChatMessage } from "../../../types";
import { queryKeys } from "../../query-keys";

const MESSAGE_PAGE = 50;

function refreshRooms(queryClient: ReturnType<typeof useQueryClient>, clubId: string) {
	return Promise.all([
		queryClient.invalidateQueries({ queryKey: queryKeys.clubPortal.chatRooms(clubId) }),
		queryClient.invalidateQueries({ queryKey: queryKeys.clubPortal.chatInvites(clubId) }),
	]);
}

export function useChatRooms(clubId: string | null) {
	return useQuery({
		enabled: !!clubId,
		queryFn: () => clubApi.chatRooms.list(clubId!),
		queryKey: queryKeys.clubPortal.chatRooms(clubId ?? ""),
		refetchInterval: 15_000,
	});
}

export function useCreateChatRoom(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (name: string) => clubApi.chatRooms.create(clubId, name),
		onSuccess: () => refreshRooms(queryClient, clubId),
	});
}

export function useRenameChatRoom(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ roomId, name }: { roomId: string; name: string }) =>
			clubApi.chatRooms.rename(clubId, roomId, name),
		onSuccess: () => refreshRooms(queryClient, clubId),
	});
}

export function useDeleteChatRoom(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (roomId: string) => clubApi.chatRooms.delete(clubId, roomId),
		onSuccess: () => refreshRooms(queryClient, clubId),
	});
}

export function useRoomMembers(clubId: string | null, roomId: string | null) {
	return useQuery({
		enabled: !!clubId && !!roomId,
		queryFn: () => clubApi.chatRooms.members(clubId!, roomId!),
		queryKey: queryKeys.clubPortal.chatRoomMembers(clubId ?? "", roomId ?? ""),
	});
}

export function useInviteToRoom(clubId: string, roomId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (userIds: string[]) => clubApi.chatRooms.invite(clubId, roomId, userIds),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: queryKeys.clubPortal.chatRoomMembers(clubId, roomId),
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.clubPortal.chatRoomInvites(clubId, roomId),
				}),
				refreshRooms(queryClient, clubId),
			]),
	});
}

export function useLeaveRoom(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (roomId: string) => clubApi.chatRooms.leave(clubId, roomId),
		onSuccess: () => refreshRooms(queryClient, clubId),
	});
}

export function useMyChatInvites(clubId: string | null) {
	return useQuery({
		enabled: !!clubId,
		queryFn: () => clubApi.chatInvites.mine(clubId!),
		queryKey: queryKeys.clubPortal.chatInvites(clubId ?? ""),
	});
}

export function useRoomInvites(clubId: string | null, roomId: string | null) {
	return useQuery({
		enabled: !!clubId && !!roomId,
		queryFn: () => clubApi.chatInvites.forRoom(clubId!, roomId!),
		queryKey: queryKeys.clubPortal.chatRoomInvites(clubId ?? "", roomId ?? ""),
	});
}

export function useSearchRoomInvitees(clubId: string, roomId: string, query: string) {
	const q = query.trim();
	return useQuery({
		enabled: q.length > 0,
		queryFn: () => clubApi.chatInvites.search(clubId, roomId, q),
		queryKey: [...queryKeys.clubPortal.chatRoomInvites(clubId, roomId), "search", q] as const,
	});
}

export function useAcceptChatInvite(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (inviteId: string) => clubApi.chatInvites.accept(clubId, inviteId),
		onSuccess: () => refreshRooms(queryClient, clubId),
	});
}

export function useDeclineChatInvite(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (inviteId: string) => clubApi.chatInvites.decline(clubId, inviteId),
		onSuccess: () => refreshRooms(queryClient, clubId),
	});
}

export function useChatMessages(clubId: string | null, roomId: string | null) {
	return useInfiniteQuery<
		RoomChatMessage[],
		Error,
		InfiniteData<RoomChatMessage[]>,
		ReturnType<typeof queryKeys.clubPortal.chatMessages>,
		string | undefined
	>({
		enabled: !!clubId && !!roomId,
		getNextPageParam: (lastPage) => {
			if (lastPage.length < MESSAGE_PAGE) return undefined;
			return lastPage.at(-1)?.created_at.toISOString();
		},
		initialPageParam: undefined,
		queryFn: ({ pageParam }) =>
			clubApi.chatMessages.list(clubId!, roomId!, {
				before: pageParam,
				limit: MESSAGE_PAGE,
			}),
		queryKey: queryKeys.clubPortal.chatMessages(clubId ?? "", roomId ?? ""),
		refetchInterval: 8_000,
	});
}

export function useSendMessage(clubId: string, roomId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (message: { content: string; replyToMessageId?: string }) =>
			clubApi.chatMessages.send(clubId, roomId, message),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.chatMessages(clubId, roomId),
			});
			refreshRooms(queryClient, clubId);
		},
	});
}

export function useEditRoomMessage(clubId: string, roomId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ messageId, content }: { messageId: string; content: string }) =>
			clubApi.chatMessages.edit(clubId, roomId, messageId, content),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.chatMessages(clubId, roomId),
			}),
	});
}

export function useSetRoomReaction(clubId: string, roomId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
			clubApi.chatMessages.react(clubId, roomId, messageId, emoji),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.chatMessages(clubId, roomId),
			}),
	});
}

export function useClearRoomReaction(clubId: string, roomId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (messageId: string) =>
			clubApi.chatMessages.clearReaction(clubId, roomId, messageId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.chatMessages(clubId, roomId),
			}),
	});
}

export function useMarkRoomRead(clubId: string, roomId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (messageId: string) => clubApi.chatMessages.markRead(clubId, roomId, messageId),
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: queryKeys.clubPortal.chatRooms(clubId) }),
	});
}
